import { authenticatedFetch } from './api';
import { API_HOST } from './constants';

/**
 * 文件工具函数库
 * 提供文件转换、下载、读取等常用功能
 */

/**
 * 将 base64 上传到 COS，返回 URL
 * @param {string} base64 - base64 Data URL
 * @returns {Promise<string>} COS URL
 */
export async function uploadBase64ToCos(base64) {
  if (!base64 || !base64.startsWith('data:')) {
    return base64; // 已经是 URL，直接返回
  }
  const url = await authenticatedFetch(`${API_HOST}/api/upload/base64`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ base64 }),
  });
  return url;
}

/**
 * 将 File 对象上传到 COS，返回 URL（先转 base64 再上传）
 * @param {File} file - File 对象
 * @returns {Promise<string>} COS URL
 */
export async function uploadFileToCos(file) {
  const base64 = await fileToBase64(file);
  return uploadBase64ToCos(base64);
}

/**
 * 将 File 对象通过 multipart 直传到 COS，返回 URL
 * 适合大文件（视频等），避免 base64 编码膨胀
 * @param {File} file - File 对象
 * @returns {Promise<string>} COS URL
 */
export async function uploadFileMultipart(file) {
  const formData = new FormData();
  formData.append('file', file);
  return authenticatedFetch(`${API_HOST}/api/upload/asset`, {
    method: 'POST',
    body: formData,
  });
}

/**
 * 将 Blob 对象转换为 Base64 Data URL
 * @param {Blob} blob - Blob 对象
 * @returns {Promise<string>} Base64 Data URL
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onloadend = () => {
      resolve(reader.result);
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to convert blob to base64'));
    };
    
    reader.readAsDataURL(blob);
  });
}

/**
 * 将 File 对象转换为 Base64 Data URL
 * @param {File} file - File 对象
 * @returns {Promise<string>} Base64 Data URL
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) {
      reject(new Error('Invalid file object'));
      return;
    }

    const reader = new FileReader();
    
    reader.onloadend = () => {
      resolve(reader.result);
    };
    
    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };
    
    reader.readAsDataURL(file);
  });
}

/**
 * 将图片 URL 转换为 Base64 Data URL
 * @param {string} url - 图片 URL
 * @returns {Promise<string|null>} Base64 Data URL，失败返回 null
 */
export async function urlToBase64(url) {
  // 如果已经是 data: 格式，直接返回
  if (url.startsWith('data:')) {
    return url;
  }

  try {
    // 使用 fetch 获取图片资源
    const response = await fetch(url, {
      mode: 'cors', // 尝试使用 CORS 模式
      cache: 'default'
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // 获取 blob 并转换为 base64
    const blob = await response.blob();
    return await blobToBase64(blob);
    
  } catch (error) {
    // 处理 CORS 错误或其他网络错误
    if (error.name === 'TypeError' || error.message.includes('CORS')) {
      console.warn(`CORS error when fetching image from ${url}:`, error.message);
    } else {
      console.error(`Failed to convert URL to base64: ${url}`, error);
    }
    return null;
  }
}

/**
 * 下载文件到本地
 * @param {string} url - 文件 URL 或 Data URL
 * @param {string} filename - 下载后的文件名
 */
export function downloadFile(url, filename) {
  // 创建临时 a 标签
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'download';
  
  // 设置隐藏样式
  link.style.display = 'none';
  
  // 添加到 DOM，触发点击，然后移除
  document.body.appendChild(link);
  
  try {
    link.click();
  } catch (error) {
    console.error('Failed to download file:', error);
    throw error;
  } finally {
    // 延迟移除，确保下载开始
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
  }
}

/**
 * 读取文本文件内容
 * @param {File} file - File 对象
 * @returns {Promise<string>} 文本内容
 */
export function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    if (!(file instanceof File)) {
      reject(new Error('Invalid file object'));
      return;
    }

    const reader = new FileReader();
    
    reader.onloadend = () => {
      resolve(reader.result);
    };
    
    reader.onerror = () => {
      reject(new Error(`Failed to read file as text: ${file.name}`));
    };
    
    reader.readAsText(file);
  });
}
